import { Stack, Typography } from "@mui/material";
import { useMemo } from "react";
import HSAFSADisclaimer from "../HSAFSADisclaimer";
import WhiteCard from "src/shared/components/WhiteCard";
import { handleFocusIntoView } from "src/utils/focusIntoView";
import { medicationInfoMap } from "../../utils/mapMedicationInfoToProduct";
import { MedicationIcon } from "src/shared/components/MedicationIcon";
import { useProduct } from "src/context/ProductProvider";
import { useSearchParams } from "react-router-dom";
import { useIsMobile } from "src/hooks/useIsMobile";
import { useTranslation } from "react-i18next";
import { theme } from "src/assets/theme";
import { ProductPrice } from "src/api/types";

interface Props {
  isQuarterly?: boolean;
  product?: ProductPrice;
  showFsaHsa?: boolean;
}

const Product = ({ isQuarterly, product, showFsaHsa = false }: Props) => {
  const isMobile = useIsMobile();
  const productSlug = useProduct((p) => p.slug);
  const category = useProduct((p) => p.category.name);
  const isGlp1 = useProduct((p) => p.is_glp1);
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const form = searchParams.get("form")?.toLowerCase();
  const variant = searchParams.get("variant")?.toLowerCase();
  const page = "payment-information";

  const formula = useMemo(() => {
    if (["aa-ot", "ac-ot", "ro-ot"].includes(productSlug)) {
      return t(`${page}.${productSlug}.formula`);
    }
    return null;
  }, [productSlug, t]);

  const medicationInfo = useMemo(() => {
    const product = `${productSlug}${
      form ? "-" + form : variant ? "-" + variant : ""
    }`;

    return medicationInfoMap[product];
  }, [form, productSlug, variant]);
  const disclaimerPath = useMemo(() => {
    if (productSlug === "iwl-ot") {
      return `${page}.${productSlug}.disclaimer`;
    }

    if (productSlug === "db-ot") return null;

    if (!isGlp1) return null;

    const duration = isQuarterly ? "3months" : "4weeks";
    return `${page}.${productSlug}.disclaimer.${duration}`;
  }, [isGlp1, isQuarterly, productSlug]);

  const text = useMemo(() => {
    if (medicationInfo.text == null) return null;

    return t(medicationInfo.text, {
      numberOfMonths: product?.number_of_months,
      monthlyPrice: product?.monthly_price,
    });
  }, [
    medicationInfo.text,
    product?.monthly_price,
    product?.number_of_months,
    t,
  ]);

  const hSAFSADisclaimerTitle = useMemo(() => {
    if (["ac-ot", "aa-ot", "ro-ot", "bb-ot", "sl-ot"].includes(productSlug)) {
      return t(`${page}.${productSlug}.disclaimer`);
    }
    return undefined;
  }, [productSlug, t]);

  return (
    <Stack sx={{ position: "relative" }}>
      <HSAFSADisclaimer
        category={category}
        title={hSAFSADisclaimerTitle}
        showHSAFSA={showFsaHsa}
      />
      <WhiteCard
        onClick={() => handleFocusIntoView("payment-input")}
        sx={{
          mt: "-15px", // pulls it up by the same radius
          borderRadius: "0 0 10px 10px",
          px: 2,
          paddingBottom: isMobile ? "20px" : "0px",
          zIndex: 1,
          cursor: "pointer",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ position: "relative", bottom: isMobile ? 0 : 10 }}
        >
          <Stack gap={1}>
            <Typography variant={isMobile ? "body1" : "h5"} fontWeight={600}>
              {t(medicationInfo.header)}
            </Typography>
            {formula ? (
              <Typography variant={isMobile ? "body2" : "inherit"}>
                {formula}
              </Typography>
            ) : null}
            {text != null ? (
              <Typography variant={isMobile ? "body2" : "h6"}>
                {text}
              </Typography>
            ) : null}
            {medicationInfo.dosage != null ? (
              <Typography variant={isMobile ? "body2" : "h6"}>
                {t(medicationInfo.dosage)}
              </Typography>
            ) : null}
            {disclaimerPath ? (
              <Typography
                color={theme.palette.primary.main}
                fontStyle="italic"
                fontWeight={600}
                fontSize={"18px"}
              >
                {t(`${disclaimerPath}`)}
              </Typography>
            ) : null}
          </Stack>
          <MedicationIcon
            name={medicationInfo.name}
            width={isMobile ? 130 : 180}
            height={isMobile ? 130 : 180}
            slug={productSlug}
          />
        </Stack>
      </WhiteCard>
    </Stack>
  );
};

export default Product;
